import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Receipt, ChevronRight } from 'lucide-react';
import { api } from '../../services/apiService';
import { hardwareApi } from '../../services/hardwareApi';
import RegisterPoiModal from './RegisterPoiModal';
import CardProcessingOptionsCard from './CardProcessingOptionsCard';
import NmiCredentialsCard from './NmiCredentialsCard';
import SurchargeAgreementsCard from './SurchargeAgreementsCard';
import TerminalCardReaderCard from './TerminalCardReaderCard';
import { RubyLoader } from '../RubyLoader';
import { CardSetupTerminalSection } from './cardSetup/CardSetupTerminalSection';
import type { CardSetupPolicy, TipLocation } from './cardSetup/cardSetupModel';
import type { DeviceModelRow } from '../../services/hardwareApi';

interface Props {
  terminal: { terminalId: string; locationId: string; locationName?: string; registerName: string };
  tenantId: string;
  onNavigateToPaymentTypes?: () => void;
  /**
   * SUNMI card setup (sunmi-card-setup.md) — present ONLY when the register's
   * catalog model carries a `cardSetupPolicy`. Then the single Card Reader card
   * is replaced by the primary/backup section; absent, this tab is unchanged.
   */
  cardSetup?: {
    model: DeviceModelRow;
    policy: CardSetupPolicy;
    reported?: string | null;
    /** The app build the till runs (e.g. android_dual_screen) — decides Single vs Dual. */
    reportedBuildTarget?: string | null;
    onModelChanged?: (modelId: string) => void;
    /** Tip location picked on Payment Types before readers were saved. */
    pendingTipLocation?: TipLocation | null;
  } | null;
}

/**
 * EMV Device tab — focused entirely on device management.
 *
 * New flow:
 *   1. Save location NMI credentials (once per location)
 *   2. Register POI devices into the location pool (independent of any
 *      payment type toggle)
 *   3. Assign a pool device to THIS terminal from Payment Types → NMI Cloud
 *      Lane. That's where the dropdown lives.
 *
 * The chicken-and-egg from the earlier design (enable NMI first vs register
 * device first) is gone: device registration only requires creds; payment-
 * type assignment picks from the already-registered pool.
 */
const PosEmvDeviceTab: React.FC<Props> = ({ terminal, tenantId, cardSetup }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gateway, setGateway] = useState<any>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [poolVersion, setPoolVersion] = useState(0);
  /** How many payment devices exist in the hardware catalog. A device must be
   *  created there before it can be registered to a location. null = not known
   *  yet (do not block on an unanswered question). */
  const [catalogCount, setCatalogCount] = useState<number | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const gw = await api.posNmi.getPaymentGateway(tenantId, terminal.locationId).catch(() => null);
      setGateway(gw);
    } catch (err: any) {
      setError(err?.message || 'Failed to load tab data');
    } finally {
      setLoading(false);
    }
  }, [tenantId, terminal.locationId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    hardwareApi.listModels({ category: 'payment_device', status: 'active' })
      .then((r) => setCatalogCount(r.count ?? (r.items || []).length))
      // Unknown, not zero — a catalog outage must not look like an empty catalog.
      .catch(() => setCatalogCount(null));
  }, []);

  if (loading) {
    return (
      <RubyLoader variant="page" className="py-16" />
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
        <AlertCircle size={28} className="mx-auto mb-3 text-red-400" />
        <p className="text-stone-600 text-sm">{error}</p>
        <button onClick={fetchAll} className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
          Retry
        </button>
      </div>
    );
  }

  const credsConfigured = !!gateway?.configured;
  const catalogEmpty = catalogCount === 0;
  const canRegister = credsConfigured && !catalogEmpty;
  const blockReason = !credsConfigured
    ? 'Save NMI credentials above before registering devices.'
    : catalogEmpty
      ? 'No payment devices in the catalog — create one in Device Management first.'
      : undefined;

  return (
    <div className="space-y-4">
      {cardSetup && (
        <CardSetupTerminalSection
          tenantId={tenantId}
          terminal={terminal}
          model={cardSetup.model}
          policy={cardSetup.policy}
          reported={cardSetup.reported}
          reportedBuildTarget={cardSetup.reportedBuildTarget}
          refreshKey={poolVersion}
          onModelChanged={cardSetup.onModelChanged}
          pendingTipLocation={cardSetup.pendingTipLocation}
        />
      )}
      {/* NMI is not a SUNMI lane — its credentials card is hidden there. */}
      {!cardSetup && <NmiCredentialsCard
        tenantId={tenantId}
        locationId={terminal.locationId}
        locationName={terminal.locationName}
        onSaved={fetchAll}
      />}

      <SurchargeAgreementsCard
        tenantId={tenantId}
        locationId={terminal.locationId}
        gateway={gateway}
        onSaved={fetchAll}
      />

      {!cardSetup && (
      <TerminalCardReaderCard
        tenantId={tenantId}
        terminal={terminal}
        refreshKey={poolVersion}
        onChanged={() => setPoolVersion(v => v + 1)}
        onRegisterClick={() => setShowRegisterModal(true)}
        canRegister={canRegister}
        blockReason={blockReason}
      />
      )}

      <CardProcessingOptionsCard
        tenantId={tenantId}
        locationId={terminal.locationId}
        gateway={gateway}
        onSaved={fetchAll}
      />

      {/* Transactions for this register — links to the full reporting page
          with terminal + tenant + today preselected. Full filtering (other
          terminals, other locations, date range, export) happens there. */}
      <button
        onClick={() => {
          const today = new Date().toISOString().slice(0, 10);
          navigate(
            `/payments?tenantId=${encodeURIComponent(tenantId)}` +
            `&terminalId=${encodeURIComponent(terminal.terminalId)}` +
            `&from=${today}&to=${today}`
          );
        }}
        className="w-full bg-white rounded-2xl shadow-sm border border-stone-200/60 px-5 py-4 flex items-center gap-3 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors text-left group"
      >
        <div className="w-10 h-10 rounded-xl bg-stone-100 group-hover:bg-emerald-100 flex items-center justify-center shrink-0 transition-colors">
          <Receipt size={18} className="text-stone-500 group-hover:text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-stone-800 font-semibold text-sm">View Transactions</p>
          <p className="text-stone-500 text-xs mt-0.5">
            Today's card transactions on <span className="font-mono text-stone-600">{terminal.registerName}</span> — filter by date, brand, status; export CSV
          </p>
        </div>
        <ChevronRight size={18} className="text-stone-400 group-hover:text-emerald-500 shrink-0" />
      </button>

      {showRegisterModal && (
        <RegisterPoiModal
          tenantId={tenantId}
          locationId={terminal.locationId}
          locationName={terminal.locationName}
          onClose={() => setShowRegisterModal(false)}
          onRegistered={async () => {
            setShowRegisterModal(false);
            setPoolVersion(v => v + 1);
            await fetchAll();
          }}
        />
      )}
    </div>
  );
};

export default PosEmvDeviceTab;
